import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'solid';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  readonly children?: ReactNode;
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly icon?: ReactNode;
  readonly style?: CSSProperties;
}

/**
 * Le socle des actions. RÉORGANISER, À FAIRE, FAIT et DIFFÉRÉ sortent tous d'ici et lisent
 * les mêmes tokens `--action-*`. Aucun appelant ne doit surcharger height, minWidth, radius,
 * border ni font — ces clés sont retirées de `style` avant application.
 */
export function Button({
  children,
  variant = 'secondary',
  size = 'md',
  icon,
  disabled,
  style,
  ...rest
}: ButtonProps) {
  const FORBIDDEN_KEYS = [
    'height',
    'minHeight',
    'width',
    'minWidth',
    'borderRadius',
    'borderWidth',
    'fontSize',
    'fontWeight',
  ] as const;
  const safeStyle: CSSProperties = Object.fromEntries(
    Object.entries(style ?? {}).filter(
      ([key]) => !(FORBIDDEN_KEYS as readonly string[]).includes(key),
    ),
  );
  const base: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--action-gap)',
    minHeight: `var(--action-h-${size})`,
    minWidth: `var(--action-minw-${size})`,
    padding: `var(--action-py) var(--action-px-${size})`,
    boxSizing: 'border-box',
    fontFamily: 'var(--font-ui)',
    fontSize: `var(--action-fs-${size})`,
    fontWeight: 'var(--action-fw)',
    letterSpacing: 'var(--tracking-label)',
    textTransform: 'uppercase',
    borderRadius: 'var(--action-radius)',
    borderWidth: 'var(--action-border-w)',
    borderStyle: 'solid',
    cursor: disabled ? 'default' : 'pointer',
    transition: 'var(--t-hover), transform var(--dur-fast) var(--ease-calm)',
    background: 'transparent',
    textAlign: 'center',
    lineHeight: 'var(--action-lh)',
  };
  const skins: Record<ButtonVariant, CSSProperties> = {
    primary: { color: 'var(--text-on-accent)', background: 'var(--accent)', borderColor: 'var(--accent)' },
    secondary: { color: 'var(--accent)', background: 'transparent', borderColor: 'var(--accent-line)' },
    ghost: { color: 'var(--text-muted)', background: 'transparent', borderColor: 'transparent' },
    solid: {
      color: 'var(--text-body)',
      background: 'var(--surface-raised)',
      borderColor: 'rgba(255,255,255,.07)',
    },
  };
  return (
    <button
      type="button"
      disabled={disabled}
      aria-disabled={disabled || undefined}
      {...rest}
      className={`ai-btn ai-btn--${variant}`}
      style={{ ...base, ...skins[variant], opacity: disabled ? 0.38 : 1, ...safeStyle }}
    >
      {icon}
      {children}
    </button>
  );
}
